import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, TrendingUp, Package } from 'lucide-react';

interface TopProduct {
  id: string;
  name: string;
  image: string | null;
  brand: string;
  price: number;
  total_sold: number;
  stock_quantity: number;
  low_stock_threshold: number;
}

const TopSellingProducts = () => {
  const { data: topProducts = [], isLoading } = useQuery({
    queryKey: ['top-selling-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('top_selling_products')
        .select('*')
        .limit(5);

      if (error) throw error;
      return data as TopProduct[];
    },
    refetchInterval: 60000, // Refetch every minute
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Top Selling Products
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-12 h-12 bg-muted rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (topProducts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Top Selling Products
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <TrendingUp className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No sales data available yet</p>
            <p className="text-sm text-muted-foreground">Sales will appear when orders are delivered</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          Top Selling Products
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {topProducts.map((product, index) => {
            const isLowStock = product.stock_quantity <= product.low_stock_threshold;
            const isOutOfStock = product.stock_quantity === 0;
            
            return (
              <div key={product.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <div className="relative">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="absolute -top-1 -left-1 w-5 h-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm truncate">{product.name}</h4>
                      <p className="text-xs text-muted-foreground">{product.brand}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-medium">
                          {product.total_sold} sold
                        </span>
                        <span className="text-xs text-muted-foreground">
                          • ₹{product.price.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-1">
                      <Badge 
                        variant={isOutOfStock ? 'destructive' : isLowStock ? 'secondary' : 'outline'}
                        className="text-xs"
                      >
                        {isOutOfStock ? 'Out of Stock' : `${product.stock_quantity} left`}
                      </Badge>
                      {isLowStock && !isOutOfStock && (
                        <Badge variant="outline" className="text-xs text-orange-600 border-orange-200">
                          Low Stock
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default TopSellingProducts;